<script setup>
import dayjs from 'dayjs'
import ja from 'dayjs/locale/ja'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import SectionTitle from '@weathernews/btos-vuecomponent/with-onebox-common-elements/src/components/SectionTitle.vue'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.tz.setDefault('Asia/Tokyo')
dayjs.locale(ja)

const props = defineProps({
  detailData: {
    type: Object,
    required: true
  }
})
const past = computed(() => props.detailData.camera_list[0])
const current = computed(() => props.detailData.camera_list.at(-1))
const range = ref(null)
const pos = ref(50)

onMounted(async () => {
  document.body.style.setProperty('--pos', pos.value + '%')
  await nextTick()
  const rangeEl = range.value
  if (rangeEl) {
    const height = rangeEl.offsetHeight
    document.body.style.setProperty('--range-height', height + 'px')
  }
})

const onInput = (e) => {
  pos.value = e.target.value
  console.log('onInput', pos.value)
  document.body.style.setProperty('--pos', pos.value + '%')
}

const onHandleDown = (e) => {
  e.preventDefault()
  const rangeEl = range.value
  if (!rangeEl) return

  function move(ev) {
    const clientX = ev.touches ? ev.touches[0].clientX : ev.clientX
    const rect = rangeEl.getBoundingClientRect()
    let percent = ((clientX - rect.left) / rect.width) * 100
    percent = Math.max(0, Math.min(100, percent))
    pos.value = Math.round(percent)
    document.body.style.setProperty('--pos', pos.value + '%')
  }
  function up() {
    window.removeEventListener('mousemove', move)
    window.removeEventListener('mouseup', up)
    window.removeEventListener('touchmove', move)
    window.removeEventListener('touchend', up)
  }
  window.addEventListener('mousemove', move)
  window.addEventListener('mouseup', up)
  window.addEventListener('touchmove', move)
  window.addEventListener('touchend', up)
  move(e)
}
</script>

<template>
  <section class="riverComparison">
    <div class="head">
      <SectionTitle title="現在と過去の様子の比較" />
    </div>
    <div class="content">
      <ClientOnly>
        <div class="compare">
          <div class="before">
            <img
              :src="past.url"
              alt=""
            >
            <p>{{ dayjs.unix(past.date).tz('Asia/Tokyo').format('M月D日 H:mm') }}</p>
          </div>
          <div class="after">
            <img
              :src="current.url"
              alt=""
            >
            <p>{{ dayjs.unix(current.date).tz('Asia/Tokyo').format('M月D日 H:mm') }}</p>
          </div>
          <input
            id="range"
            ref="range"
            v-model="pos"
            type="range"
            min="0"
            max="100"
            step="1"
            @input="onInput"
          >
          <span
            class="handle"
            :style="{ left: `calc(${pos}% + (50 - ${pos})/50 * 1px)` }"
            @mousedown="onHandleDown"
            @touchstart="onHandleDown"
          />
        </div>
      </ClientOnly>
    </div>
  </section>
</template>

<style lang="scss" scoped>
.riverComparison {
  display: flex;
  flex-direction: column;
  gap: 10px;
  .head {
    display: flex;
  }
  .content {
    position: relative;
    width: 100%;
    aspect-ratio: 16 / 9;
    overflow: hidden;
    .compare {
      width: 100%;
      height: 100%;
      .before {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        background-color: #303030;
        place-content: center;
        img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
        p {
          position: absolute;
          top: 6px;
          left: 6px;
          padding: 5px 10px;
          border-radius: 4px;
          color: #303030;
          background-color: rgb(238 241 248 / .8);
          box-shadow: 0 1px 2px 0 rgb(53 105 192 / .4);
          font-size: 12px;
          font-weight: 300;
          line-height: 1em;
          letter-spacing: .25px;
        }
      }
      .after {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        background-color: #303030;
        place-content: center;
        mask: linear-gradient(to right, #ffffff00 0, calc(var(--pos, 50%)), #ffffff 0);
        img {
          width: 100%;
          height: 100%;
          object-fit: contain;
        }
        p {
          position: absolute;
          top: 6px;
          right: 6px;
          padding: 5px 10px;
          border-radius: 4px;
          color: #303030;
          background-color: rgb(238 241 248 / .8);
          box-shadow: 0 1px 2px 0 rgb(53 105 192 / .4);
          font-size: 12px;
          font-weight: 300;
          line-height: 1em;
          letter-spacing: .25px;
        }
      }
      input[type="range"] {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        margin: 0px;
        z-index: 1;
        appearance: none;
        background: transparent;
        cursor: pointer;
        &::-webkit-slider-thumb {
          appearance: none;
          width: 2px;
          height: var(--range-height);
          background-color: #ffffff;
        }

        &::-moz-range-thumb {
          appearance: none;
          width: 2px;
          height: var(--range-height);
          background-color: #ffffff;
        }
      }
      .handle {
        position: absolute;
        z-index: 2;
        pointer-events: auto;
        top: 50%;
        left: 50%;
        transform: translate(-18px, -18px);
        color: #000;
        background: rgba(255, 255, 255, 0.5);
        width: 36px;
        height: 36px;
        border-radius: 50%;
        &:before {
          content: '';
          position: absolute;
          width: 10px;
          height: 10px;
          top: 50%;
          border-top: solid 2px;
          border-left: solid 2px;
          transform-origin: 0 0;
          left: 5px;
          transform: rotate(-45deg);
        }
        &:after {
          content: '';
          position: absolute;
          width: 10px;
          height: 10px;
          top: 50%;
          border-top: solid 2px;
          border-right: solid 2px;
          transform-origin: right top;
          right: 5px;
          transform: rotate(45deg);
        }
      }
    }
  }
}
@media (min-width: 830px) {
  .riverComparison {
    .content {
      .compare{
        .before {
          p {
            font-size: 14px;
            font-weight: 600;
          }
        }
        .after {
          p {
            font-size: 14px;
            font-weight: 600;
          }
        }
      }
    }
  }
}
</style>
